import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map, take, startWith } from 'rxjs/operators';

import {
  ControlData,
  FinnairBoundItem,
  FinnairCheckInEligibility,
  FinnairFrequentFlyerCardItem,
  FinnairOrder,
  FinnairPassengerCode,
  FinnairPassengerItem,
} from '@fcom/dapi/api/models';
import { GaContext } from '@fcom/common/interfaces';
import { ButtonPosition, ButtonTheme } from '@fcom/ui-components/index';
import { isTruthy } from '@fcom/core/index';

import { PassengerWithCheckInInfo } from '../interface/check-in.interface';
import { hasUnrecoverableEligibilityForPassenger } from '../../../utils/check-in.utils';

export type PassengerForm = FormGroup<Record<string, FormControl<boolean>>>;

@Component({
  selector: 'fin-check-in-passenger-details',
  templateUrl: './check-in-passenger-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckInPassengerDetailsComponent implements OnInit, OnDestroy {
  readonly GaContext = GaContext;
  readonly ButtonPosition = ButtonPosition;
  readonly ButtonTheme = ButtonTheme;

  @Input() order$: Observable<FinnairOrder>;
  @Input() bound$: Observable<FinnairBoundItem>;
  @Input() passengers$: Observable<FinnairPassengerItem[]>;
  @Input() mainPaxInfo$: Observable<FinnairPassengerItem>;
  @Input() controlData$: Observable<ControlData>;
  @Input() allFrequentFlyerCards$: Observable<FinnairFrequentFlyerCardItem[]>;
  @Input() confirmButtonLoading$: Observable<boolean>;
  @Input() modifyCheckIn = false;
  @Input() isAPISRequired$: Observable<boolean>;

  @Output() confirmPassengers: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() prefetchSeatmap: EventEmitter<FinnairPassengerItem> = new EventEmitter<FinnairPassengerItem>();

  applyForCheckInForm: PassengerForm;
  updatingPassengers: string[] = [];
  passengersWithCheckinInfo$: Observable<PassengerWithCheckInInfo[]>;
  notCheckedInPassengers$: Observable<PassengerWithCheckInInfo[]>;
  checkedInPassengers$: Observable<PassengerWithCheckInInfo[]>;
  allPaxSelectedCheckboxCheckedByDefault$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  confirmEnabled$: Observable<boolean>;

  private subscriptions: Subscription = new Subscription();

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    const checkInEligibilityForBound$: Observable<FinnairCheckInEligibility> = combineLatest([
      this.order$,
      this.bound$,
    ]).pipe(
      map(([order, bound]) => order?.eligibilities?.checkIn?.find((eligibility) => eligibility.id === bound?.id))
    );

    this.passengersWithCheckinInfo$ = combineLatest([
      checkInEligibilityForBound$.pipe(
        map((checkInEligibilityForBound) => checkInEligibilityForBound?.flightTravelerEligibility ?? {})
      ),
      this.passengers$,
    ]).pipe(
      map(([checkinFlightEligibilities, passengers]) => {
        return passengers
          .map((passenger) => {
            // TODO: This does not take into account multi flight different eligibilities
            const passengerEligibility = Object.values(checkinFlightEligibilities)
              .flatMap((flightCheckinEligibility) =>
                flightCheckinEligibility.find((eligibility) => eligibility.travelerIds.includes(passenger.id))
              )
              .filter(Boolean)[0];
            return {
              ...passengers.find((pax) => pax.id === passenger.id),
              withInfant: this.getInfantAssociation(passengers, passenger),
              isCheckedIn: passengerEligibility?.isCheckedIn ?? false,
              isAllowedToUseCheckIn: passengerEligibility?.isAllowedToUseCheckIn ?? false,
              isAllowedToUseBoardingPass: passengerEligibility?.isAllowedToUseBoardingPass ?? false,
              hasUnrecoverableReason: hasUnrecoverableEligibilityForPassenger(passengerEligibility),
            };
          })
          .filter(({ passengerTypeCode }) => passengerTypeCode !== FinnairPassengerCode.INF);
      })
    );
    this.notCheckedInPassengers$ = this.passengersWithCheckinInfo$.pipe(
      map((passengers) => passengers.filter((passenger) => !passenger.isCheckedIn))
    );
    this.checkedInPassengers$ = this.passengersWithCheckinInfo$.pipe(
      map((passengers) => passengers.filter((passenger) => passenger.isCheckedIn))
    );

    this.subscriptions.add(
      combineLatest([this.notCheckedInPassengers$, this.checkedInPassengers$])
        .pipe(take(1))
        .subscribe(([passengersNotCheckedIn, passengersCheckedIn]) => {
          if (!this.modifyCheckIn) {
            this.applyForCheckInForm = this.createForm(passengersNotCheckedIn, (passenger) => {
              return new FormControl({
                value: !passenger.isCheckedIn && passenger.isAllowedToUseCheckIn,
                disabled: passenger.isCheckedIn || !passenger.isAllowedToUseCheckIn,
              });
            });
            this.allPaxSelectedCheckboxCheckedByDefault$.next(true);
          } else {
            this.applyForCheckInForm = this.createForm(passengersCheckedIn, () => {
              return new FormControl(false);
            });
          }
        })
    );

    this.confirmEnabled$ = this.applyForCheckInForm.valueChanges.pipe(
      map(() => {
        return Object.keys(this.applyForCheckInForm.controls).some((id) => this.applyForCheckInForm.get(id).value);
      }),
      startWith(Object.keys(this.applyForCheckInForm.controls).some((id) => this.applyForCheckInForm.get(id).value))
    );
  }

  private getInfantAssociation(passengers: FinnairPassengerItem[], passenger: FinnairPassengerItem): string | null {
    const infant = passengers.find(({ accompanyingTravelerId }) => accompanyingTravelerId === passenger.id);
    if (infant) {
      return `${infant.firstName} ${infant.lastName}`;
    }
    return null;
  }

  private createForm(
    passengers: (FinnairPassengerItem & {
      isCheckedIn: boolean;
      isAllowedToUseCheckIn: boolean;
    })[],
    createFormControl: (passenger) => FormControl<boolean>
  ): PassengerForm {
    const hasPassengersSelected = (control: AbstractControl) => {
      if (!Object.values(control.value).some(isTruthy)) {
        return { custom: true };
      }
      return null;
    };

    return new FormGroup(
      passengers.reduce(
        (all, passenger) => {
          all[passenger.id] = createFormControl(passenger);
          return all;
        },
        {} as Record<string, FormControl>
      ),
      hasPassengersSelected
    );
  }

  checkInAllPassengers(checkInAllPassengers: boolean): void {
    Object.keys(this.applyForCheckInForm.controls)
      .filter((passengerId) => !this.applyForCheckInForm.get(passengerId).disabled)
      .forEach((key) => {
        this.applyForCheckInForm.controls[key].setValue(checkInAllPassengers);
      });
  }

  confirmPassengersStep(): void {
    this.confirmPassengers.emit(
      Object.keys(this.applyForCheckInForm.controls).filter(
        (passengerId) => this.applyForCheckInForm.get(passengerId).value
      )
    );
  }

  getAccompanyingInfant(passenger: FinnairPassengerItem): Observable<FinnairPassengerItem> {
    return this.order$.pipe(
      take(1),
      map((order) => order.passengers.find((p) => passenger.id === p.accompanyingTravelerId))
    );
  }

  getAccompanyingAdult(passenger: FinnairPassengerItem): Observable<FinnairPassengerItem> {
    return this.order$.pipe(
      take(1),
      map((order) => order.passengers.find((p) => p.id === passenger.accompanyingTravelerId))
    );
  }

  setUpdatingPassenger(passenger: FinnairPassengerItem, updating: boolean): void {
    if (updating) {
      this.updatingPassengers.push(passenger.id);
    } else {
      this.updatingPassengers = this.updatingPassengers.filter((i) => i !== passenger.id);
      this.prefetchSeatmap.emit(passenger);
    }
  }
}
